import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';

/**
 * Exit-intent popup — apare cand userul muta mouse-ul spre top (gata sa inchida tab-ul)
 * SAU dupa 90 secunde de inactivitate (mobile fallback).
 *
 * Se afiseaza o singura data per session (sessionStorage).
 *
 * Props:
 *   - onDemoClick: () => void — callback pentru "Solicita demo"
 */
export default function ExitIntentPopup({ onDemoClick }) {
  // keyPrefix — cheile stau sub `app.modals.exit_intent` in translation.json
  const { t } = useTranslation('translation', { keyPrefix: 'app.modals.exit_intent' });
  const [visible, setVisible] = useState(false);
  // Folosim useRef in loc de useState pentru `dismissed` ca sa nu re-rulam effect-ul
  // la fiecare update (cu state, deps array trigger re-attach listener si dublu popup).
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem('pb_exit_intent_shown')) {
      dismissedRef.current = true;
      return;
    }

    const showPopup = () => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      sessionStorage.setItem('pb_exit_intent_shown', '1');
      setVisible(true);
    };

    const handleMouseLeave = (e) => {
      if (e.clientY < 5) showPopup();
    };

    const timeoutId = setTimeout(showPopup, 60000);
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timeoutId);
    };
  }, []);

  const close = () => setVisible(false);

  if (!visible) return null;

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(13,27,42,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        animation: 'fadeIn 0.3s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: 20,
          maxWidth: 480,
          width: '100%',
          padding: '40px 32px',
          textAlign: 'center',
          position: 'relative',
          boxShadow: '0 30px 80px rgba(13,27,42,0.4)',
          border: '1px solid rgba(212,175,55,0.2)',
          animation: 'slideUp 0.4s ease',
        }}
      >
        <button
          onClick={close}
          aria-label={t('close_aria')}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: '#F3F4F6',
            border: 'none',
            width: 32,
            height: 32,
            borderRadius: '50%',
            fontSize: 18,
            cursor: 'pointer',
            color: '#6B7280',
          }}
        >
          ×
        </button>

        <div style={{ fontSize: 64, marginBottom: 16 }}>👋</div>

        <p style={{ fontSize: 12, fontWeight: 600, color: '#8B6914', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 12, fontFamily: 'DM Mono, monospace' }}>
          {t('eyebrow')}
        </p>

        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 700, color: '#0A1428', marginBottom: 16, lineHeight: 1.2 }}>
          <Trans
            i18nKey="app.modals.exit_intent.title"
            components={{ 1: <span style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} /> }}
          />
        </h2>

        <p style={{ fontSize: 16, color: '#4B5563', lineHeight: 1.6, marginBottom: 28, fontFamily: 'Inter, system-ui, sans-serif' }}>
          <Trans i18nKey="app.modals.exit_intent.body" components={{ 1: <strong /> }} />
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={() => { close(); onDemoClick && onDemoClick(); }}
            style={{
              padding: '14px 24px',
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)',
              color: '#0A1428',
              border: 'none',
              borderRadius: 10,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 8px 20px rgba(212,175,55,0.3)',
            }}
          >
            {t('cta_demo')}
          </button>
          <Link
            to="/register"
            onClick={close}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              color: '#0A1428',
              textDecoration: 'none',
              border: '1.5px solid #0A1428',
              borderRadius: 10,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 14,
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {t('cta_try_free')}
          </Link>
        </div>

        <p style={{ marginTop: 24, fontSize: 11, color: '#6B7280', fontFamily: 'DM Mono, monospace', letterSpacing: 0.5 }}>
          {t('privacy_note')}
        </p>
      </div>
    </div>
  );
}
